var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Checkbox } from '@paysera/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { addDays } from 'date-fns';
import { useLazyGetExpectedProfitQuery, useSubmitInvestmentMutation, useValidateInvestmentAmountMutation, } from '@app/api/investmentsApi';
import { useGetSpecialConditionsMutation } from '@app/api/specialConditionsApi';
import { ErrorMessage } from '@app/components/ErrorMessage';
import { InlineSpinner } from '@app/components/InlineSpinner';
import FullyInvestedError from '@app/components/popups/FullyInvested';
import InvestmentComplete from '@app/components/popups/InvestmentComplete';
import { DATE_FORMAT } from '@app/configs/dateTime';
import { getLegalUrl } from '@app/configs/documentsLists';
import { fullyInvestedErrorMessage, INVESTMENT_AMOUNT_CONSTANTS, tooBigAmountErrorMessage, } from '@app/configs/investment';
import { ROUTES } from '@app/configs/routes';
import useBoolean from '@app/hooks/useBoolean';
import useDebounceValue from '@app/hooks/useDebounceValue';
import { getErrorMessage } from '@app/utils/getErrorMessage';
import { getUserLocale } from '@app/utils/getUserLocale';
import { getLocalDateTime } from '@app/utils/time';
import formatAmount from '@modules/calculator/utils/formatAmount';
import { AmountRangeInput } from '@modules/investor/components/common/AmountRangeInput';
import { TAB_KEYS } from '@modules/investor/config/tabs';
import { investmentFormValidationSchema } from '@modules/investor/validation/investmentFormValidationSchema';
const { MIN_AMOUNT, MAX_AMOUNT, STEP } = INVESTMENT_AMOUNT_CONSTANTS;
const InvestmentForm = ({ details, refetchInvestmentDetails, loadingDetails, children }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const locale = getUserLocale();
    const [isOpenSuccessModal, { setTrue: openSuccessModal, setFalse: closeSuccessModal }] = useBoolean(false);
    const amountLeft = (details === null || details === void 0 ? void 0 : details.amountLeft) ? Number(details.amountLeft) : 0;
    const maxInvestmentAmount = amountLeft >= MAX_AMOUNT ? MAX_AMOUNT : amountLeft;
    const [isFullyInvested, { setTrue: openFullyInvestedModal, setFalse: closeFullyInvestedModal }] = useBoolean(false);
    const [validateAmount, { isLoading: validatingAmount, error: validateError }] = useValidateInvestmentAmountMutation();
    const typedValidateError = validateError;
    const [submitInvestment, { isLoading: submittingInvestment, error: submitError }] = useSubmitInvestmentMutation();
    const typedSubmitError = submitError;
    const [getSpecialConditions, { isLoading: loadingSpecialConditions }] = useGetSpecialConditionsMutation();
    const { register, handleSubmit, watch, setValue, trigger, formState: { errors }, } = useForm({
        resolver: yupResolver(investmentFormValidationSchema(maxInvestmentAmount)),
        defaultValues: {
            investmentAmount: MIN_AMOUNT,
            generalConditions: false,
            specialConditions: false,
        },
    });
    const isInvestButtonDisabled = submittingInvestment || validatingAmount || loadingDetails;
    const investmentAmount = watch('investmentAmount');
    const debouncedValidateAmount = useDebounceValue(investmentAmount, 1000);
    const [getExpectedProfit, { data: expectedProfitData, isLoading: loadingExpectedProfit, isFetching: fetchingExpectedProfit },] = useLazyGetExpectedProfitQuery();
    const handleRangeChange = (value) => {
        setValue('investmentAmount', value);
        trigger('investmentAmount');
    };
    useEffect(() => {
        if (debouncedValidateAmount >= MIN_AMOUNT && debouncedValidateAmount <= MAX_AMOUNT) {
            validateAmount({ amount: debouncedValidateAmount.toString() });
            getExpectedProfit({ id: Number(id), investmentAmount: debouncedValidateAmount });
        }
    }, [debouncedValidateAmount]);
    useEffect(() => {
        if (amountLeft < investmentAmount) {
            handleRangeChange(amountLeft);
        }
    }, [amountLeft]);
    const onSubmitSuccess = (data) => {
        if (!(data === null || data === void 0 ? void 0 : data.error)) {
            openSuccessModal();
            return;
        }
        if (data === null || data === void 0 ? void 0 : data.error) {
            const errorMessage = getErrorMessage(data === null || data === void 0 ? void 0 : data.error);
            if (errorMessage === fullyInvestedErrorMessage) {
                openFullyInvestedModal();
            }
            if (errorMessage === tooBigAmountErrorMessage) {
                refetchInvestmentDetails();
            }
        }
    };
    const onSubmit = (data) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!((_a = typedValidateError === null || typedValidateError === void 0 ? void 0 : typedValidateError.data) === null || _a === void 0 ? void 0 : _a.amount)) {
            submitInvestment({
                amount: data.investmentAmount.toString(),
                loanId: Number(id),
                isGeneralConditionsAccepted: data.generalConditions,
                isSpecialConditionsAccepted: data.specialConditions,
            }).then(onSubmitSuccess);
        }
    });
    const handleCloseSuccessModal = () => {
        closeSuccessModal();
        navigate(`${ROUTES.home}?tab=${TAB_KEYS.MY_INVESTMENTS}`, { replace: true });
    };
    const handleCloseFullyInvestedModal = () => {
        closeFullyInvestedModal();
        navigate(ROUTES.home, { replace: true });
    };
    const handleValidateAmount = () => {
        trigger('investmentAmount');
    };
    const investmentDeadline = details
        ? getLocalDateTime(addDays(new Date(details.fundingDate), 4).toISOString(), DATE_FORMAT.dateTime)
        : '';
    const handleOpenSpecialConditions = (event) => {
        event.preventDefault();
        getSpecialConditions({ amount: `${investmentAmount}`, loanId: Number(id) });
    };
    const expectedProfit = (expectedProfitData === null || expectedProfitData === void 0 ? void 0 : expectedProfitData.expected_profit) ? formatAmount(expectedProfitData.expected_profit) : '0';
    return (<form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 w-full">
            <div className="flex flex-col gap-6">
                {children}
                <div className="flex flex-col gap-3 pb-6 border-b border-neutral-primary-initial">
                    <h2 className="title-m text-neutral-primary-initial">
                        {t('financing.investment_details.investment')}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                        <div className="flex flex-col gap-0.5">
                            <p className="label-s text-neutral-secondary-initial">
                                {t('financing.investment_details.interest_rate')}
                            </p>
                            <p className="title-s text-neutral-primary-initial">
                                {details ? `${details.annualRate}%` : ''}
                            </p>
                        </div>
                        <div className="flex flex-col gap-0.5">
                            <p className="label-s text-neutral-secondary-initial">
                                {t('financing.investment_details.investors')}
                            </p>
                            <p className="title-s text-neutral-primary-initial">
                                {details ? details.investmentCount : ''}
                            </p>
                        </div>
                        <div className="flex flex-col gap-0.5">
                            <p className="label-s text-neutral-secondary-initial">
                                {t('financing.investment_details.investment_deadline')}
                            </p>
                            <p className="title-s text-neutral-primary-initial">{investmentDeadline}</p>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-3 pb-6 border-b border-neutral-primary-initial">
                    <h2 className="title-m text-neutral-primary-initial">
                        {t('financing.investment_details.investment_amount')}
                    </h2>
                    <AmountRangeInput min={MIN_AMOUNT} max={maxInvestmentAmount} step={STEP} value={investmentAmount} onChange={handleRangeChange} error={((_a = errors.investmentAmount) === null || _a === void 0 ? void 0 : _a.message) || ((_b = typedValidateError === null || typedValidateError === void 0 ? void 0 : typedValidateError.data) === null || _b === void 0 ? void 0 : _b.amount)} validateAmountError={handleValidateAmount}/>
                    {((_c = typedValidateError === null || typedValidateError === void 0 ? void 0 : typedValidateError.data) === null || _c === void 0 ? void 0 : _c.amount) && (<ErrorMessage error={t('financing.investment_details.insufficient_funds', {
                balance: typedValidateError.data.amount,
            })}/>)}
                    {((_d = errors.investmentAmount) === null || _d === void 0 ? void 0 : _d.message) === 'min_amount' && (<ErrorMessage error={t('financing.investment_details.min_amount', {
                amount: formatAmount(MIN_AMOUNT, true, false),
            })}/>)}
                    {((_e = errors.investmentAmount) === null || _e === void 0 ? void 0 : _e.message) === 'max_amount' && (<ErrorMessage error={t('financing.investment_details.max_amount', {
                amount: formatAmount(maxInvestmentAmount, true, false),
            })}/>)}
                    {((_f = errors.investmentAmount) === null || _f === void 0 ? void 0 : _f.message) === 'amount_step' && (<ErrorMessage error={t('financing.investment_details.amount_step', {
                step: formatAmount(STEP, true, false),
            })}/>)}
                    {((_g = typedSubmitError === null || typedSubmitError === void 0 ? void 0 : typedSubmitError.data) === null || _g === void 0 ? void 0 : _g.errorMessage) === 'investment_amount_too_big' && (<ErrorMessage error={t('financing.investment_details.investment_amount_too_big')}/>)}
                </div>

                <div className="flex flex-col gap-3">
                    <h2 className="title-m text-neutral-primary-initial">
                        {t('financing.investment_details.linked_account')}
                    </h2>
                    <div className="flex flex-col gap-0.5">
                        <p className="label-s text-neutral-secondary-initial">
                            {t('financing.investment_details.account_info')}
                        </p>
                        <p className="title-s text-neutral-primary-initial font-mono tracking-wider">
                            {details ? details.walletNumber : ''}
                        </p>
                    </div>
                </div>
            </div>

            <aside className="flex flex-col gap-4 p-6 rounded-lg bg-surface-neutral-secondary-initial h-fit">
                <div className="flex flex-col gap-0.5 pb-4 border-b border-neutral-primary-initial">
                    <p className="body-s text-neutral-primary-initial">
                        {t('financing.investment_details.expected_profit')}
                    </p>
                    <p className="headline-s text-neutral-primary-initial">
                        {loadingExpectedProfit || fetchingExpectedProfit ? (<InlineSpinner isLoading/>) : (`≈ ${expectedProfit}`)}
                    </p>
                </div>

                <div className="flex flex-col gap-2 pb-4 border-b border-neutral-primary-initial">
                    <p className="title-s text-neutral-primary-initial">
                        {t('financing.investment_details.more_info')}
                    </p>
                    <a href={getLegalUrl('serviceFee', locale)} target="_blank" className="body-l text-action-primary-initial" rel="noreferrer">
                        {t('financing.investment_details.fees_for_service')}
                    </a>
                    <a href={getLegalUrl('creditworthinessRules', locale)} target="_blank" rel="noopener noreferrer" className="body-l text-action-primary-initial">
                        {t('financing.investment_details.creditworthiness_rules')}
                    </a>
                </div>

                <div className="flex flex-col gap-2">
                    <p className="title-s text-neutral-primary-initial">
                        {t('financing.investment_details.ive_read_and_agreed')}
                    </p>
                    <Checkbox className="min-w-4 items-start" variant={errors.generalConditions ? 'critical' : 'default'} {...register('generalConditions')} label={<a href={getLegalUrl('generalConditions', locale)} target="_blank" rel="noopener noreferrer" className="title-s text-action-primary-initial">
                                {t('financing.investment_details.general_conditions')}
                            </a>}/>
                    <Checkbox className="min-w-4 items-start" variant={errors.specialConditions ? 'critical' : 'default'} {...register('specialConditions')} label={<span className="inline-flex gap-1 items-baseline">
                                <button type="button" onClick={handleOpenSpecialConditions} className="title-s text-action-primary-initial">
                                    {t('financing.investment_details.special_conditions')}
                                </button>
                                <InlineSpinner isLoading={loadingSpecialConditions}/>
                            </span>}/>
                    {(errors.generalConditions || errors.specialConditions) && (<p className="body-s text-critical-primary-initial">
                            {t('financing.investment_details.terms_required')}
                        </p>)}
                </div>

                <Button type="submit" variant="primary" size="lg" isFullWidth disabled={isInvestButtonDisabled} loading={isInvestButtonDisabled} label={t('financing.investment_details.invest')}/>
            </aside>

            <FullyInvestedError isOpen={isFullyInvested} closeModal={handleCloseFullyInvestedModal}/>
            <InvestmentComplete isOpen={isOpenSuccessModal} closeModal={handleCloseSuccessModal}/>
        </form>);
};
export default InvestmentForm;
